import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import MobiusPage from "@insite/mobius/Page";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadOrder from "@WhiteCap/Handlers/LoadOrder";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    loadOrder,
};

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        isLoading: state.WhiteCap.customOrderStatusPage.isLoading,
        error: state.WhiteCap.customOrderStatusPage.error,
    };
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

export interface OrderStatusPageStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    errorMessage?: TypographyProps;
}

export const orderStatusPageStyles: OrderStatusPageStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    errorMessage: {
        variant: "h2",
        weight: 500,
        css: css`
            margin-top: 40px;
            text-align: center;
        `,
    },
};

const styles = orderStatusPageStyles;

const CustomOrderStatusPage = ({ id, loadOrder, isLoading, error }: Props) => {

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const orderId = urlParams.get("orderId");
        const q = urlParams.get("Q");
        if (orderId) {
            // Make API call
            loadOrder({
                orderId,
                q
            });
        }
    }, []);

    return (
        <MobiusPage>
            {error ? (
                <Typography {...styles.errorMessage}>{error}</Typography>
            ) : (
                <>
                    {isLoading ? (
                        <StyledWrapper {...styles.centeringWrapper}>
                            <LoadingSpinner {...styles.spinner} />
                        </StyledWrapper>
                    ) : (
                        <Zone contentId={id} zoneName="Content" requireRows />
                    )}
                </>
            )}
        </MobiusPage>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CustomOrderStatusPage)),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;
